import {
  doc, collection, setDoc, addDoc, updateDoc, deleteDoc, getDocs,
  serverTimestamp, writeBatch, runTransaction, increment,
} from "firebase/firestore";
import { db, auth } from "../lib/firebase.js";
import { COL, SUB, SETTINGS_DOC_ID } from "./collections.js";
import { deleteImage } from "./storage.js";

const uid = () => auth.currentUser?.uid || null;
const stamp = () => ({ updatedAt: serverTimestamp(), updatedBy: uid() });

/* ---------- Settings ---------- */
export async function saveSettings(patch) {
  await setDoc(doc(db, COL.siteSettings, SETTINGS_DOC_ID), { ...patch, ...stamp() }, { merge: true });
}

/* ---------- Pages + sections ---------- */
/* sections: array of { id?, type, order, ...fields }. Writes page meta + diffs the
   sections subcollection (upsert current, delete removed). */
export async function savePage(slug, { meta = {}, sections = [] }) {
  const batch = writeBatch(db);
  batch.set(doc(db, COL.pages, slug), { slug, ...meta, ...stamp() }, { merge: true });

  const secRef = collection(db, COL.pages, slug, SUB.sections);
  const existing = await getDocs(secRef);
  const keepIds = new Set();
  sections.forEach((s, i) => {
    const id = s.id || doc(secRef).id;
    keepIds.add(id);
    const { id: _omit, ...fields } = s;
    batch.set(doc(secRef, id), { ...fields, order: i });
  });
  existing.forEach((d) => {
    if (!keepIds.has(d.id)) batch.delete(doc(secRef, d.id));
  });
  await batch.commit();
}

/* ---------- Programs ---------- */
export async function saveProgram(slug, data) {
  await setDoc(doc(db, COL.programs, slug), { slug, ...data, ...stamp() }, { merge: true });
}
export async function deleteProgram(slug, heroStoragePath) {
  await deleteImage(heroStoragePath);
  await deleteDoc(doc(db, COL.programs, slug));
}

/* ---------- Events ---------- */
export async function saveEvent(slug, data) {
  const ref = doc(db, COL.events, slug);
  await setDoc(
    ref,
    {
      slug,
      ...data,
      totals: data.totals || { pledgedCount: 0, receivedAmount: 0 },
      createdAt: data.createdAt || serverTimestamp(),
      ...stamp(),
    },
    { merge: true }
  );
}
export async function deleteEvent(slug) {
  await deleteDoc(doc(db, COL.events, slug));
}

/* ---------- Inbound (public creates) ---------- */
export async function addLead(data) {
  return addDoc(collection(db, COL.donorLeads), {
    ...data,
    status: "new",
    linkedDonationId: null,
    submittedAt: serverTimestamp(),
  });
}
export async function updateLeadStatus(id, status) {
  await updateDoc(doc(db, COL.donorLeads, id), { status });
}

export async function addEnquiry(data) {
  return addDoc(collection(db, COL.enquiries), {
    ...data,
    status: "new",
    submittedAt: serverTimestamp(),
  });
}
export async function updateEnquiryStatus(id, status) {
  await updateDoc(doc(db, COL.enquiries, id), { status });
}

/* ---------- School club/group registrations ---------- */
/* Public create (form submission). Starts as `pending` awaiting admin review. */
export async function addSchoolGroup(data) {
  return addDoc(collection(db, COL.schoolGroups), {
    ...data,
    status: "pending",
    submittedAt: serverTimestamp(),
  });
}
/* Approve / reject a pending submission. Records who reviewed and when. */
export async function updateSchoolGroupStatus(id, status) {
  await updateDoc(doc(db, COL.schoolGroups, id), {
    status,
    reviewedAt: serverTimestamp(),
    reviewedBy: uid(),
  });
}
/* Admin edits a group's details after registration. */
export async function saveSchoolGroup(id, data) {
  await updateDoc(doc(db, COL.schoolGroups, id), { ...data, ...stamp() });
}
export async function deleteSchoolGroup(id) {
  await deleteDoc(doc(db, COL.schoolGroups, id));
}

/* ---------- Donations ---------- */
export async function saveDonation(id, data) {
  if (id) {
    await updateDoc(doc(db, COL.donations, id), { ...data, ...stamp() });
    return id;
  }
  const ref = await addDoc(collection(db, COL.donations), {
    ...data,
    createdAt: serverTimestamp(),
    ...stamp(),
  });
  return ref.id;
}

/* Mark a donation received and (transactionally) bump the linked event's total. */
export async function markDonationReceived(donationId, { receivedDate, amount, eventId }) {
  await runTransaction(db, async (tx) => {
    const dRef = doc(db, COL.donations, donationId);
    tx.update(dRef, { status: "received", receivedDate, ...stamp() });
    if (eventId) {
      const eRef = doc(db, COL.events, eventId);
      tx.update(eRef, { "totals.receivedAmount": increment(Number(amount) || 0) });
    }
  });
}

/* ---------- Blog posts ---------- */
export async function savePost(id, data) {
  if (id) {
    await updateDoc(doc(db, COL.blogPosts, id), { ...data, ...stamp() });
    return id;
  }
  const ref = await addDoc(collection(db, COL.blogPosts), {
    ...data,
    createdAt: serverTimestamp(),
    ...stamp(),
  });
  return ref.id;
}
export async function deletePost(id, featuredImageStoragePath) {
  await deleteImage(featuredImageStoragePath);
  await deleteDoc(doc(db, COL.blogPosts, id));
}

/* ---------- Gallery ---------- */
export async function saveGalleryItem(id, data) {
  if (id) {
    await updateDoc(doc(db, COL.gallery, id), { ...data, ...stamp() });
    return id;
  }
  const ref = await addDoc(collection(db, COL.gallery), { ...data, createdAt: serverTimestamp(), ...stamp() });
  return ref.id;
}
export async function deleteGalleryItem(id, imageStoragePath) {
  await deleteImage(imageStoragePath);
  await deleteDoc(doc(db, COL.gallery, id));
}
